import React, { useState } from "react";

import { Icon } from "@/shared/components/icon/Icon";
import { Badge, BadgeProps, Text } from "@orgmym/leeloo-core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import type { AttachmentStatus } from "@/features/chat/shared/types/media/media";
import type { ChatMention } from "@/features/chat/shared/types";
import { MentionText } from "@/features/chat/shared/components/mention-text";

import * as styles from "./media-offer-card.css";
import { useMediaOfferImages } from "@/features/chat/shared/hooks";
import { formatDuration } from "@/shared/utils/time.utils";

const STATUS_TRANSLATION_PREFIX =
  "creators_messaging_chat_private_media_status";

const STATUS_TRANSLATION_SUFFIX_MAP: Record<AttachmentStatus, string> = {
  waiting_for_payment: "delivered",
  refused_by_fan: "refused",
  refused_by_creator: "refused",
  refused: "refused",
  delivered: "purchased",
  paid: "paid",
  purchased: "purchased",
  pending: "pending",
  to_deliver: "to_deliver"
};

export interface MediaOfferImage {
  id: string;
  url: string;
  type?: string;
  position: number;
  duration?: number;
  thumbnail_url?: string;
}

export interface MediaOfferPrice {
  amount: number;
  currency: string;
}

export interface LiveOfferInfo {
  duration: string;
  formattedPrice: string;
  bookingUrl?: string;
  onJoin?: () => void;
}

export interface MediaOfferCardProps {
  description: string;
  price?: MediaOfferPrice | number;
  formattedPrice?: string;
  status: AttachmentStatus;
  isPaid: boolean;
  position?: "left" | "right";
  images?: MediaOfferImage[];
  teaser?: MediaOfferImage;
  onImageClick?: (imageId: string) => void;
  transactionId?: string;
  className?: string;
  showLockOnTeaser?: boolean;
  forceShowLock?: boolean;
  mediaCounts?: { image: number; video: number };
  readStatus?: "read" | "unread";
  currency?: string;
  maxDescriptionLength?: number;
  timeLabel?: string;
  messageLabel?: string;
  promoText?: string;
  mentions?: ChatMention[];
  liveOffer?: LiveOfferInfo;
  headerSlot?: React.ReactNode;
  quotedDescription?: boolean;
  hidePriceBadge?: boolean;
  statusBadgeLabel?: string;
}

export const MediaOfferCard: React.FC<MediaOfferCardProps> = ({
  description,
  price,
  formattedPrice,
  status,
  isPaid,
  position = "right",
  images,
  teaser,
  className,
  onImageClick,
  forceShowLock = false,
  mediaCounts,
  readStatus,
  maxDescriptionLength = 200,
  timeLabel,
  messageLabel,
  promoText,
  mentions,
  liveOffer,
  headerSlot,
  quotedDescription = false,
  hidePriceBadge = false,
  statusBadgeLabel
}) => {
  const { t } = useTranslation();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const { mainImage, thumbnailImages } = useMediaOfferImages({
    images,
    teaser
  });

  const isOwner = position === "right";

  const showLockOnMainImage =
    !isOwner && mainImage !== teaser && (forceShowLock || !isPaid);

  const showLockOnThumbnails = !isOwner && (forceShowLock || !isPaid);

  const totalMediaCount = (mediaCounts?.image ?? 0) + (mediaCounts?.video ?? 0);

  const isFree =
    price === undefined ||
    price === null ||
    (typeof price === "number" ? price === 0 : price.amount === 0);

  const getStatusLabel = (status: AttachmentStatus): string => {
    const suffix = STATUS_TRANSLATION_SUFFIX_MAP[status];
    return t(`${STATUS_TRANSLATION_PREFIX}_${suffix}`);
  };

  const shouldTruncateDescription = description.length > maxDescriptionLength;
  const showReadMore = shouldTruncateDescription;
  const displayDescription = isDescriptionExpanded
    ? description
    : description.slice(0, maxDescriptionLength);

  const showImages = teaser || (images && images.length > 0);

  const getStatusBadge = (): {
    variant: BadgeProps["variant"];
    label: string;
  } | null => {
    if (isFree)
      return {
        variant: "brand-light",
        label: t("creators_messaging_chat_private_media_status_free", {
          defaultValue: "Gratuit"
        })
      };
    switch (status) {
      case "pending":
      case "waiting_for_payment":
      case "to_deliver":
        return { variant: "neutral", label: getStatusLabel(status) };
      case "refused":
      case "refused_by_fan":
      case "refused_by_creator":
        return { variant: "danger", label: getStatusLabel(status) };
      case "delivered":
      case "paid":
      case "purchased":
        return { variant: "success", label: getStatusLabel(status) };
      default:
        return null;
    }
  };

  const computedStatusBadge = getStatusBadge();
  const statusBadge =
    statusBadgeLabel && computedStatusBadge
      ? { ...computedStatusBadge, label: statusBadgeLabel }
      : computedStatusBadge;

  const isRefusedStatus =
    status === "refused" ||
    status === "refused_by_creator" ||
    status === "refused_by_fan";
  // Hide the status badge on the fan side for clarity, except when the
  // request was refused — there we keep it visible.
  const showStatusBadge = position !== "left" || isRefusedStatus;

  return (
    <div
      className={styles.wrapper({ position })}
      data-testid="media-offer-card-wrapper"
    >
      <div
        className={clsx(styles.container({ position, status }), className)}
        data-testid="media-offer-card"
        data-media-offer-card-type="media"
      >
        {showImages && (
          <div
            className={styles.imagesContainer}
            data-testid="media-offer-card-images"
          >
            {mainImage && (
              <div
                className={styles.mainImageContainer}
                onClick={() => onImageClick?.(mainImage.id)}
                data-testid={`media-offer-card-image-main-${mainImage.id}`}
              >
                <img
                  src={mainImage.thumbnail_url || mainImage.url}
                  alt={
                    teaser
                      ? t("media_offer_card.image.alt.teaser", {
                          defaultValue: "Teaser preview"
                        })
                      : t("media_offer_card.image.alt.media", {
                          defaultValue: "Media {{position}}",
                          position: mainImage.position
                        })
                  }
                  className={styles.mainImage}
                  loading="lazy"
                />
                {showLockOnMainImage && (
                  <div className={styles.imageOverlay}>
                    <div className={styles.lockIconContainer}>
                      <Icon
                        name="lock"
                        className={styles.lockIcon}
                        style={{ width: "24px", height: "24px" }}
                      />
                    </div>
                  </div>
                )}
                {totalMediaCount > 0 && (
                  <div className={styles.priceBadgeOverlay}>
                    <div className={styles.priceBadge}>
                      {!isFree && formattedPrice && (
                        <Text className={styles.priceBadgeText}>
                          {formattedPrice}
                        </Text>
                      )}
                      {(mediaCounts?.image ?? 0) > 0 && (
                        <div className={styles.priceBadgeGroup}>
                          <Icon
                            name="picture"
                            className={styles.priceBadgeIcon}
                            style={{ width: "12px", height: "12px" }}
                          />
                          <Text className={styles.priceBadgeText}>
                            {mediaCounts!.image}
                          </Text>
                        </div>
                      )}
                      {(mediaCounts?.video ?? 0) > 0 && (
                        <div className={styles.priceBadgeGroup}>
                          <Icon
                            name="camera"
                            className={styles.priceBadgeIcon}
                            style={{ width: "12px", height: "12px" }}
                          />
                          <Text className={styles.priceBadgeText}>
                            {mediaCounts!.video}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {mainImage.type === "video" && mainImage.duration ? (
                  <div className={styles.videoDuration}>
                    <Text className={styles.videoDurationText}>
                      {formatDuration(mainImage.duration)}
                    </Text>
                  </div>
                ) : null}
                {teaser && (
                  <Badge
                    variant="neutral"
                    size="sm"
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      zIndex: 2
                    }}
                    data-testid="media-offer-card-teaser-badge"
                  >
                    <Text className={styles.baseTeaserBadgeText}>
                      {t("fan_messaging_chat_push_teaser_logo", {
                        defaultValue: "Teaser"
                      })}
                    </Text>
                  </Badge>
                )}
              </div>
            )}

            {promoText && (
              <div className={styles.promoBanner}>
                <Icon name="coupon" style={{ width: "20px", height: "20px" }} />
                <Text className={styles.promoBannerText}>{promoText}</Text>
              </div>
            )}

            {thumbnailImages.length > 0 && (
              <div className={styles.thumbnailsGrid}>
                {thumbnailImages.map((image) => (
                  <div
                    key={image.id}
                    className={styles.thumbnailContainer}
                    onClick={() => onImageClick?.(image.id)}
                    data-testid={`media-offer-card-image-thumb-${image.id}`}
                  >
                    <img
                      src={image.thumbnail_url || image.url}
                      alt={t("media_offer_card.image.alt.media", {
                        defaultValue: "Media {{position}}",
                        position: image.position
                      })}
                      className={styles.thumbnailImage}
                      loading="lazy"
                    />
                    {showLockOnThumbnails && (
                      <div className={styles.imageOverlay}>
                        <div className={styles.lockIconContainerThumbnail}>
                          <Icon
                            name="lock"
                            className={styles.lockIconThumbnail}
                          />
                        </div>
                      </div>
                    )}
                    <div className={styles.thumbnailIconContainer}>
                      <Icon
                        name={image.type === "video" ? "camera" : "picture"}
                        size="small"
                      />
                    </div>
                    {image.type === "video" && image.duration ? (
                      <div className={styles.videoDuration}>
                        <Text className={styles.videoDurationText}>
                          {formatDuration(image.duration)}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {description &&
          (quotedDescription ? (
            <div
              className={styles.quotedDescriptionContainer}
              data-testid="media-offer-card-description"
            >
              <Text className={styles.quotedDescriptionText}>
                {description}
              </Text>
            </div>
          ) : (
            <div className={styles.descriptionContainer}>
              <Text
                className={clsx(
                  styles.description,
                  showReadMore &&
                    !isDescriptionExpanded &&
                    styles.descriptionTruncated
                )}
                data-testid="media-offer-card-description"
              >
                {displayDescription}
                {showReadMore && !isDescriptionExpanded && "..."}
              </Text>
              {showReadMore && (
                <button
                  type="button"
                  className={styles.readMoreButton}
                  onClick={() =>
                    setIsDescriptionExpanded(!isDescriptionExpanded)
                  }
                  data-testid="media-offer-card-read-more-button"
                >
                  {isDescriptionExpanded
                    ? t("creators_messaging_chat_private_media_read_less", {
                        defaultValue: "Voir moins"
                      })
                    : t("creators_messaging_chat_private_media_read_more", {
                        defaultValue: "Lire plus"
                      })}
                </button>
              )}
            </div>
          ))}

        {headerSlot}

        {liveOffer && (
          <div
            className={styles.liveOfferContainer}
            data-testid="media-offer-card-live-info"
          >
            <div className={styles.liveOfferRow}>
              <Text className={styles.liveOfferLabel}>
                {t("NSFW_Live_1_1_creator_calendar_steptwo_service_form_label")}
              </Text>
              <Text className={styles.liveOfferValue}>
                {liveOffer.duration}
              </Text>
            </div>
            <div className={styles.liveOfferRow}>
              <Text className={styles.liveOfferLabel}>
                {t(
                  "NSFW_Live_1_1_creator_calendar__steptwo__service_form_price"
                )}
              </Text>
              <Text className={styles.liveOfferValue}>
                {liveOffer.formattedPrice}
              </Text>
            </div>
            {liveOffer.onJoin && (
              <button
                type="button"
                className={styles.liveJoinButton}
                onClick={liveOffer.onJoin}
                data-testid="media-offer-card-live-join"
              >
                {t("NSFW_live_pretest_bottombar_button")}
              </button>
            )}
          </div>
        )}

        <div
          className={styles.statusContainer}
          data-testid="media-offer-card-status"
        >
          {statusBadge && showStatusBadge && (
            <Badge
              variant={statusBadge.variant}
              size="sm"
              data-testid="media-offer-card-status-badge"
            >
              <Text
                className={styles.teaserBadgeText({
                  status: status
                })}
              >
                {statusBadge.label}
              </Text>
            </Badge>
          )}
          {(timeLabel || messageLabel || readStatus) && (
            <div className={styles.statusIconContainer}>
              {timeLabel && (
                <Text
                  as="time"
                  className={styles.inlineTimestamp}
                  data-testid="media-offer-card-timestamp"
                >
                  {timeLabel}
                </Text>
              )}
              {messageLabel && (
                <Text variant="caption" className={styles.inlineMessageLabel}>
                  {messageLabel}
                </Text>
              )}
              {readStatus && (
                <Icon
                  name={
                    readStatus === "read" ? "read-message" : "unread-message"
                  }
                  className={styles.statusIcon({ readStatus })}
                  aria-label={
                    readStatus === "read"
                      ? t("media_offer_card.status.read_aria", {
                          defaultValue: "Read"
                        })
                      : t("media_offer_card.status.unread_aria", {
                          defaultValue: "Unread"
                        })
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
